"use client";

import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, HeartPulse, Phone, ShieldAlert, Stethoscope } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const emergencySymptoms = [{
  title: "Severe breathing distress",
  desc: "Rapid shallow panting, open-mouth breathing in cats, or blue-tinted gums."
}, {
  title: "Suspected toxin ingestion",
  desc: "Swallowing human medications, chocolate, lilies, rodent bait, or antifreeze."
}, {
  title: "Sudden collapse or weakness",
  desc: "Inability to stand, disorientation, sudden paralysis, or active seizures."
}, {
  title: "Unproductive retching",
  desc: "Swollen hard abdomen, repeated dry heaving, or acute abdominal pain."
}, {
  title: "Straining to urinate",
  desc: "Frequent painful posturing with little to no urine output (critical in male cats)."
}, {
  title: "Severe trauma or hemorrhage",
  desc: "Vehicle impact, deep lacerations, persistent bleeding, or eye injuries."
}];
const generalConcerns = ["Persistent vomiting or diarrhea exceeding 24 hours", "Noticeable appetite loss lasting more than one full day", "Sudden changes in water intake or litter box behavior", "Unexplained limping, stiffness, or reluctance to move"];
export default function VetContact() {
  return <section id="when-to-contact-your-vet" data-nav="light" className="w-full bg-background py-12 md:py-16 lg:py-20">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Main Clinical Governance Alert Callout */}
        <div className="relative overflow-hidden rounded-r-xl rounded-l-none border-y border-r border-border border-l-4 border-l-chart-1 bg-card p-6 sm:p-8 md:p-10 lg:p-12 shadow-sm">
          
          {/* Header Metadata & Badge */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-border">
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-lg bg-background text-chart-1 shadow-2xs border border-border">
                <ShieldAlert className="size-5" />
              </span>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-chart-1">
                  Clinical Guidance
                </span>
                <p className="text-xs text-muted-foreground">
                  Reviewed under veterinary triage protocols
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground">
              <HeartPulse className="size-3.5 text-destructive" />
              <span>Emergency triage indicator</span>
            </div>
          </div>

          {/* Core Title and Editorial Lead */}
          <div className="mt-6 max-w-3xl">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground">
              When to seek immediate veterinary care
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Some symptoms require urgent medical intervention rather than watchful waiting at home. Contact an emergency clinic right away if you observe any red-flag signs in your pet.
            </p>
          </div>

          {/* Symptom Matrix Grid */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="mb-4 flex items-center gap-2">
              <AlertCircle className="size-4 text-destructive" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                Critical Red-Flag Symptoms
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {emergencySymptoms.map((item, i) => <div key={item.title} data-index={i} className="rounded-lg border border-border bg-background p-4.5 transition-[border-color,box-shadow] hover:border-chart-1/50 hover:shadow-xs">
                  <div className="flex items-start gap-2.5">
                    <span className="mt-1 size-2 shrink-0 rounded-full bg-destructive" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {item.title}
                      </h4>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Secondary Non-Emergency Consultation List */}
          <div className="mt-8 rounded-lg border border-border bg-background/60 p-5 sm:p-6">
            <h4 className="text-sm font-semibold text-foreground">
              Schedule a standard appointment within 24 to 48 hours for:
            </h4>
            <ul className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {generalConcerns.map((point, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                  <CheckCircle2 className="size-4 shrink-0 text-chart-1 mt-0.5" />
                  <span>{point}</span>
                </li>)}
            </ul>
          </div>

          {/* Reassurance & Action Strip */}
          <div className="mt-8 flex flex-col items-start justify-between gap-6 border-t border-border pt-6 sm:flex-row sm:items-center">
            <div className="max-w-xl">
              <p className="text-sm text-foreground font-medium">
                Trust your instincts as a pet caregiver.
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                If something feels noticeably wrong, a quick phone call to your local veterinary clinic is always the safest course of action.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
              <Button asChild variant="default" size="default" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/PetHealthPage" className="inline-flex items-center justify-center gap-2">
                  <Stethoscope className="size-4" />
                  <span>Explore health topics</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="default" className="w-full sm:w-auto border-border bg-background text-foreground hover:bg-muted">
                <Link to="/AboutPetsWhirlPage" className="inline-flex items-center justify-center gap-2">
                  <span>About our review standards</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}